/**
 * SkillMatrix — career-progression heatmap.
 *
 * Reads `insights.skills[]` + `insights.level_metadata` from the role-insights
 * JSON. Each row is a canonical skill, each column is a level, each cell is
 * the extent expected at that intersection.
 *
 * Phase 0 visual encoding (Option A from the design doc):
 *   - Cell fill color → extent (familiar < proficient < expert < lead)
 *   - Cell opacity → confidence (high = solid, low = washed out)
 *   - Empty cells → "—" with neutral background
 *   - Hover → tooltip with frequency + sample wording
 *
 * Skills are grouped by `skill_categories` for visual structure.
 */
import React, { useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { SAGE, CREAM } from '../../theme';

const F = "'Outfit', sans-serif";

// Extent → fill color (sage gradient lightest→darkest), with `lead` taking
// a slightly different hue so it reads as a step beyond `expert`.
const EXTENT_FILL = {
  familiar:   { bg: SAGE[100], fg: SAGE[700], label: 'Familiar' },
  proficient: { bg: SAGE[300], fg: SAGE[800], label: 'Proficient' },
  expert:     { bg: SAGE[500], fg: '#fff',    label: 'Expert' },
  lead:       { bg: SAGE[800], fg: CREAM[50], label: 'Lead' },
};

const CONFIDENCE_OPACITY = { high: 1, medium: 0.78, low: 0.55 };

const ORDER = ['familiar', 'proficient', 'expert', 'lead'];

const ExtentCell = ({ entry, levelMeta, skillLabel }) => {
  if (!entry) {
    return (
      <Box
        sx={{
          height: 38,
          borderRadius: 1.25,
          bgcolor: `${SAGE[100]}40`,
          border: `1.5px dashed ${SAGE[200]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: SAGE[300],
          fontFamily: F,
          fontSize: '0.7rem',
        }}
      >
        —
      </Box>
    );
  }
  const fill = EXTENT_FILL[entry.extent] || EXTENT_FILL.proficient;
  const opacity = CONFIDENCE_OPACITY[entry.confidence] ?? 0.85;
  const freqPct = Math.round((entry.frequency || 0) * 100);
  const wording = (entry.wordings && entry.wordings[0]) || '';

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: F, mb: 0.5 }}>
            {skillLabel} · {levelMeta?.name || ''}
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', fontFamily: F, mb: 0.5 }}>
            <strong>{fill.label}</strong> · seen in {freqPct}% of postings · confidence {entry.confidence}
          </Typography>
          {wording && (
            <Typography sx={{ fontSize: '0.7rem', fontFamily: F, fontStyle: 'italic', opacity: 0.85 }}>
              "{wording}"
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          height: 38,
          borderRadius: 1.25,
          bgcolor: fill.bg,
          color: fill.fg,
          opacity,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: F,
          fontSize: '0.72rem',
          fontWeight: 600,
          letterSpacing: 0.3,
          cursor: 'default',
          transition: 'transform 0.12s',
          '&:hover': { transform: 'scale(1.04)' },
        }}
      >
        {fill.label.toLowerCase()}
      </Box>
    </Tooltip>
  );
};

const Legend = () => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mt: 2 }}>
    {ORDER.map((k) => {
      const f = EXTENT_FILL[k];
      return (
        <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: 0.75, bgcolor: f.bg }} />
          <Typography sx={{ fontSize: '0.72rem', color: SAGE[500], fontFamily: F }}>
            {f.label}
          </Typography>
        </Box>
      );
    })}
    <Box sx={{ flex: 1 }} />
    <Typography sx={{ fontSize: '0.7rem', color: SAGE[400], fontFamily: F, fontStyle: 'italic' }}>
      Cell opacity reflects curator confidence
    </Typography>
  </Box>
);

const SkillMatrix = ({ insights }) => {
  const [hoveredSkillId, setHoveredSkillId] = useState(null);

  if (!insights?.skills?.length || !insights?.level_metadata) return null;

  const skills = insights.skills;
  const levelMeta = insights.level_metadata;
  const levelOrder = Object.keys(levelMeta);
  const categories = insights.skill_categories || [];
  const isPlaceholder = insights.skill_matrix_provenance === 'phase-0-placeholder';

  // Group skills by category in the declared order
  const skillsByCategory = categories.map((cat) => ({
    ...cat,
    skills: skills.filter((s) => s.category === cat.id),
  }));
  // Catch any skills whose category isn't in the declared list
  const declaredCatIds = new Set(categories.map((c) => c.id));
  const orphanSkills = skills.filter((s) => !declaredCatIds.has(s.category));
  if (orphanSkills.length) {
    skillsByCategory.push({ id: '__other__', label: 'Other', skills: orphanSkills });
  }

  const gridTemplate = `220px repeat(${levelOrder.length}, minmax(120px, 1fr))`;

  return (
    <Box>
      {isPlaceholder && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            bgcolor: '#fdf2e9',
            border: '1px solid #f0c896',
            display: 'inline-block',
          }}
        >
          <Typography sx={{ fontSize: '0.7rem', color: '#a86532', fontFamily: F, fontWeight: 600 }}>
            Placeholder data — visual only, not curated
          </Typography>
        </Box>
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          gap: 0.75,
          mb: 1,
          px: 0.5,
        }}
      >
        <Box />
        {levelOrder.map((id) => {
          const meta = levelMeta[id];
          return (
            <Box key={id} sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: SAGE[800], fontFamily: F }}>
                {meta?.name || id}
              </Typography>
              <Typography sx={{ fontSize: '0.66rem', color: SAGE[400], fontFamily: F }}>
                {meta?.yoe_range}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Body — grouped by category */}
      {skillsByCategory.map((cat, ci) => (
        <Box key={cat.id} sx={{ mb: ci === skillsByCategory.length - 1 ? 0 : 1.25 }}>
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              color: SAGE[500],
              fontFamily: F,
              mb: 0.5,
              ml: 0.5,
            }}
          >
            {cat.label}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {cat.skills.map((skill) => {
              const isHovered = hoveredSkillId === skill.skill_id;
              return (
                <Box
                  key={skill.skill_id}
                  onMouseEnter={() => setHoveredSkillId(skill.skill_id)}
                  onMouseLeave={() => setHoveredSkillId(null)}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    gap: 0.75,
                    alignItems: 'center',
                    px: 0.5,
                    py: 0.5,
                    borderRadius: 1.25,
                    bgcolor: isHovered ? `${SAGE[100]}80` : 'transparent',
                    transition: 'background 0.1s',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      color: SAGE[800],
                      fontFamily: F,
                      fontWeight: isHovered ? 600 : 500,
                      pr: 1,
                    }}
                  >
                    {skill.label}
                  </Typography>
                  {levelOrder.map((lvId) => (
                    <ExtentCell
                      key={lvId}
                      entry={skill.by_level?.[lvId]}
                      levelMeta={levelMeta[lvId]}
                      skillLabel={skill.label}
                    />
                  ))}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}

      <Legend />
    </Box>
  );
};

export default SkillMatrix;
